import { Injectable } from '@angular/core'
import { LogService } from 'htv-lib'
import {
  LocalChannelEPG,
  LocalChannelLiveEPG,
  LocalChannelLiveEPGProgram,
  LocalEPGProgram,
} from '../interfaces/local-epg'
import { HumecloudService } from './humecloud.service'
import { EpgChannelDataDto, EpgProgramDataDto } from '../interfaces/dto/epg-dto'
import { LocalChannel } from '../interfaces/local-channel'
import { StorageService } from './storage.service'
import moment from 'moment'

@Injectable({
  providedIn: 'root',
})
export class EpgService {
  channels: LocalChannel[] = []

  channelsEPG: { [key: string]: LocalChannelEPG } = {}
  loadedChannelIds: string[] = []
  loadedStart?: Date
  loadedEnd?: Date

  refreshTimer: any
  readonly refreshTimerDelayInMs = 3600000

  readonly channelsEPGKey = 'EPGChannels'
  readonly savedChannelIdsKey = 'EPGChannelIds'
  readonly savedStartKey = 'EPGStart'
  readonly savedEndKey = 'EPGEnd'

  constructor(
    private humecloudService: HumecloudService,
    private logService: LogService,
    private storageService: StorageService
  ) {
    // do nothing
  }

  init(channels: LocalChannel[]): void {
    this.logService.debug('[EpgService] Initializing from list of channels')
    this.channels = channels

    // Load saved values
    const channelsEPG: { [key: string]: LocalChannelEPG } = this.storageService.getJson(this.channelsEPGKey)
    // Fix dates loaded as string...
    if (channelsEPG) {
      for (const channelId in channelsEPG) {
        const channelEPG = channelsEPG[channelId]
        if (channelEPG.dataStart) channelEPG.dataStart = new Date(channelEPG.dataStart)
        if (channelEPG.dataEnd) channelEPG.dataEnd = new Date(channelEPG.dataEnd)
        for (const program of channelEPG.programs) {
          program.start = new Date(program.start)
          program.end = new Date(program.end)
          program.duration = this.getProgramDuration(program.start, program.end)
        }
      }
      this.channelsEPG = channelsEPG
    }

    this.loadedChannelIds = this.storageService.getJson(this.savedChannelIdsKey)
    this.loadedStart = this.storageService.getDate(this.savedStartKey)
    this.loadedEnd = this.storageService.getDate(this.savedEndKey)

    // Check if we need to force refresh
    const providedChannelIds = channels.map(c => c.globalChannelId)
    const forceRefresh = JSON.stringify(this.loadedChannelIds) != JSON.stringify(providedChannelIds)

    this.refresh(channels, forceRefresh)
    this.registerRefreshTimer()
  }

  registerRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }
    this.refreshTimer = setTimeout(() => {
      this.refresh(this.channels, false)
      this.registerRefreshTimer()
    }, this.refreshTimerDelayInMs)
  }

  async refresh(channels: LocalChannel[], force: boolean): Promise<void> {
    this.logService.debug(`[EpgService] refreshing data... (force = ${force})`)

    const channelIds = channels.map(c => c.globalChannelId)

    const startDate = new Date()
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + 1)
    endDate.setHours(23, 59, 59, 999)

    if (
      !force &&
      this.loadedStart &&
      this.loadedEnd &&
      startDate >= this.loadedStart &&
      startDate < this.loadedEnd &&
      endDate >= this.loadedStart &&
      endDate <= this.loadedEnd
    ) {
      this.logService.debug(`EPG data for these dates has already been loaded`)
      return
    }

    try {
      const data = await this.retrieveEpg(channelIds, startDate, endDate)

      const channelsEPG: { [key: string]: LocalChannelEPG } = {}
      channels.forEach(channel => {
        const channelEPGDto = data.find(d => d.globalChannelId == channel.globalChannelId)
        if (channelEPGDto) {
          const mappedPrograms = channelEPGDto.programs.map((program: EpgProgramDataDto): LocalEPGProgram => {
            const programStart = new Date(program.start)
            const programEnd = new Date(program.end)
            return {
              start: programStart,
              end: programEnd,
              duration: this.getProgramDuration(programStart, programEnd),
              translations: program.translations,
              defaultTranslationCode: program.defaultTranslationCode,
            }
          })

          const uniqueProgramsMap = new Map<string, LocalEPGProgram>()
          mappedPrograms.forEach(program => {
            const key = `${program.start.getTime()}-${program.end.getTime()}`
            if (!uniqueProgramsMap.has(key)) {
              uniqueProgramsMap.set(key, program)
            }
          })

          const programs = Array.from(uniqueProgramsMap.values()).sort((a, b) => a.start.getTime() - b.start.getTime())

          let minDate = undefined
          let maxDate = undefined
          if (programs.length > 0) {
            const dates = programs.map(p => p.start.getTime())
            minDate = new Date(Math.min(...dates))
            maxDate = new Date(Math.max(...dates))
          }

          channelsEPG[channel.id] = {
            channelId: channel.id,
            globalChannelId: channel.globalChannelId,
            programs: programs,
            dataStart: minDate,
            dataEnd: maxDate,
          }
        }
      })

      this.channelsEPG = channelsEPG
      this.loadedChannelIds = channelIds
      this.loadedStart = startDate
      this.loadedEnd = endDate

      this.storageService.saveJson(this.channelsEPGKey, this.channelsEPG)
      this.storageService.saveJson(this.savedChannelIdsKey, this.loadedChannelIds)
      this.storageService.saveDate(this.savedStartKey, this.loadedStart)
      this.storageService.saveDate(this.savedEndKey, this.loadedEnd)

      this.logService.debug(`[EpgService] Data refreshed`)
    } catch {
      this.logService.error(`[EpgService] Could not refresh EPG data, aborting`)
    }
  }

  retrieveEpg(channelIds: string[], startDate: Date, endDate: Date): Promise<EpgChannelDataDto[]> {
    return new Promise((resolve, reject) => {
      this.humecloudService.getEpgData(channelIds, startDate, endDate, []).subscribe({
        next: (data: EpgChannelDataDto[]) => {
          resolve(data)
        },
        error: () => {
          reject()
        },
      })
    })
  }

  getChannelEPG(channelId: string): LocalChannelEPG | undefined {
    if (this.channelsEPG) {
      return this.channelsEPG[channelId]
    } else {
      return undefined
    }
  }

  // We assume here that the order of programs is chronogically correct
  getChannelLiveEPG(channelId: string): LocalChannelLiveEPG | undefined {
    const channelEPG = this.getChannelEPG(channelId)
    if (channelEPG) {
      const now = new Date()
      const currentProgramIndex = channelEPG.programs.findIndex(({ start, end }) => {
        return start <= now && end > now
      })
      const currentProgram = channelEPG.programs[currentProgramIndex]
      const nextProgram = channelEPG.programs[currentProgramIndex + 1]

      const liveEPG: LocalChannelLiveEPG = {
        channelId: channelId,
      }

      const mapToLiveEPGProgram = (program: LocalEPGProgram): LocalChannelLiveEPGProgram => {
        const translation = program.translations[program.defaultTranslationCode]
        return {
          start: program.start,
          end: program.end,
          title: translation?.title ?? '',
          description: translation?.description ?? '',
        }
      }

      if (currentProgram) {
        liveEPG.currentProgram = mapToLiveEPGProgram(currentProgram)
      }
      if (nextProgram) {
        liveEPG.nextProgram = mapToLiveEPGProgram(nextProgram)
      }

      return liveEPG
    } else {
      return undefined
    }
  }

  getProgramDuration(start: Date, end: Date): number {
    return moment(end).diff(start, 'minutes')
  }
}
